// api/approver.ts — approver-only endpoints. The portal backend proxies these
// to sitemanager with the signed-in approver's identity; sitemanager performs
// the NetBird-affecting write and re-validates group coverage.

import { api } from './client';
import type { ApproverGrant, GroupSitesPreview, GrantStatus, SiteAddition } from './types';

const BASE = '/api/approver';

export function listApproverGrants(status?: GrantStatus): Promise<ApproverGrant[]> {
    const q = status ? `?status=${encodeURIComponent(status)}` : '';
    return api<ApproverGrant[]>(`${BASE}/grants${q}`);
}

export function getApproverGrant(id: string): Promise<ApproverGrant> {
    return api<ApproverGrant>(`${BASE}/grants/${encodeURIComponent(id)}`);
}

export function approverGroupSites(groupId: string): Promise<GroupSitesPreview> {
    return api<GroupSitesPreview>(`${BASE}/groups/${encodeURIComponent(groupId)}/sites`);
}

function post<T>(path: string, body: unknown): Promise<T> {
    return api<T>(path, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify(body),
    });
}

export function approveGrant(id: string, expiresAt: string): Promise<unknown> {
    return post(`${BASE}/grants/${encodeURIComponent(id)}/approve`, { expiresAt });
}

export function rejectGrant(id: string, reason?: string): Promise<unknown> {
    return post(`${BASE}/grants/${encodeURIComponent(id)}/reject`, { reason });
}

export function renewGrant(id: string, expiresAt: string): Promise<unknown> {
    return post(`${BASE}/grants/${encodeURIComponent(id)}/renew`, { expiresAt });
}

export function revokeGrant(id: string): Promise<unknown> {
    return post(`${BASE}/grants/${encodeURIComponent(id)}/revoke`, {});
}

// ── Site-group additions (flow 4) ───────────────────────────────────────

export function listSiteAdditions(): Promise<SiteAddition[]> {
    return api<SiteAddition[]>(`${BASE}/site-additions`);
}

export function approveSiteAddition(id: string): Promise<unknown> {
    return post(`${BASE}/site-additions/${encodeURIComponent(id)}/approve`, {});
}

export function rejectSiteAddition(id: string, reason?: string): Promise<unknown> {
    return post(`${BASE}/site-additions/${encodeURIComponent(id)}/reject`, { reason });
}

// The backend returns `{ error }` JSON on 4xx; HttpError.message carries the
// raw body. Pull out the human message for inline display.
export function grantErrorMessage(e: unknown): string {
    const msg = (e as Error)?.message ?? 'Something went wrong';
    try {
        const parsed = JSON.parse(msg);
        if (parsed && typeof parsed.error === 'string') return parsed.error;
    } catch { /* not JSON — use as-is */ }
    return msg;
}
